import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Json } from '@/integrations/supabase/types';
import {
  type PersistedConnectionPlatform,
  type SocialAnalyticsSyncPlatform,
  isPersistedConnectionPlatform,
  isSocialAnalyticsSyncPlatform,
} from '@/lib/platforms/connection-capabilities';
import { getPlatformLabel } from '@/lib/platforms/identity';
import { withRevenueRollupInclusion } from '@/lib/platforms/revenue-rollup';
import type { ShopifyScopeSnapshot } from '@/lib/platforms/shopify-permissions';

export interface RefreshResult {
  success: boolean;
  message?: string;
  error?: string;
}

export interface ConnectionScopeCheckResult {
  grantedScopes: string[];
  declinedScopes: string[];
  checkedAt: string | null;
}

export interface TransferConnectionResult {
  connectionId: string;
  fromBrandKitId: string;
  toBrandKitId: string;
}

export interface ShopifyConnectionTarget {
  /** Durable user_connections row to use for this request. */
  connectionId?: string | null;
  /** Active workspace that must own the connection. */
  workspaceId?: string | null;
  /** Store domain used by the Shopify install enrichment path. */
  shopDomain?: string | null;
}

export interface SyncStatus {
  platform: string;
  source_count: number;
  hub_count: number;
  synced: boolean;
}

type SyncStatusRpc = (fn: 'get_connection_sync_status') => Promise<{
  data: SyncStatus[] | null;
  error: { message?: string } | null;
}>;

/**
 * Validated against the 2026-04 Admin GraphQL schema by `@shopify/dev-mcp`
 * (`validate_graphql_codeblocks`, ✅ VALID) on 2026-08-27. Do not retype it from
 * memory — this repo has a documented history of guessing Shopify APIs wrong.
 */
const SHOPIFY_ACCESS_SCOPES_QUERY = `query MoraShopifyGrantedScopes {
  currentAppInstallation {
    accessScopes { handle }
    app {
      requestedAccessScopes { handle }
      optionalAccessScopes { handle }
    }
  }
}`;

export class IntegrationService extends BaseService {
  private static instance: IntegrationService;

  private constructor() {
    super({
      defaultTTL: 5 * 60 * 1000, // 5 minutes
      enableCache: false, // Disable caching for integration operations
      enableRetry: true,
    });
  }

  public static getInstance(): IntegrationService {
    if (!IntegrationService.instance) {
      IntegrationService.instance = new IntegrationService();
    }
    return IntegrationService.instance;
  }

  /**
   * Verify the sync status between spoke and hub tables.
   */
  async verifySyncStatus(): Promise<ServiceResponse<SyncStatus[]>> {
    return this.execute(async () => {
      const callSyncStatus = this.client.rpc as unknown as SyncStatusRpc;
      const { data, error } = await callSyncStatus('get_connection_sync_status');
      if (error) {
        throw new Error(error.message || 'Failed to verify sync status');
      }
      return data ?? [];
    }, 'Failed to verify sync status');
  }

  /**
   * Refresh a Shopify connection
   */
  /**
   * The connected store's own identity — its REAL display name and primary
   * storefront domain. Onboarding's Shopify-install path uses this instead of
   * guessing the name from the myshopify subdomain, and the primary CUSTOM
   * domain gives the brand scan far richer material than *.myshopify.com.
   */
  async getShopifyShopInfo(
    target: ShopifyConnectionTarget = {},
  ): Promise<ServiceResponse<{ name: string | null; primaryDomainUrl: string | null }>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke('shopify-admin-proxy', {
        body: {
          shopifyApiUrlPath: '/admin/api/2026-04/graphql.json',
          method: 'POST',
          connection_id: target.connectionId ?? undefined,
          brand_kit_id: target.workspaceId ?? undefined,
          shop_domain: target.shopDomain ?? undefined,
          body: { query: '{ shop { name primaryDomain { url } } }' },
        },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Failed to load Shopify shop info');
      }

      const shop = data.data?.data?.shop as
        { name?: string; primaryDomain?: { url?: string } } | undefined;
      return {
        name: shop?.name ?? null,
        primaryDomainUrl: shop?.primaryDomain?.url ?? null,
      };
    }, 'Failed to load Shopify shop info');
  }

  /**
   * The scopes this store has ACTUALLY granted, asked live.
   *
   * 🔴 Never read `user_connections.connection_data.scopes` for this. That column
   * records what the token exchange returned at install and is never refreshed,
   * so it cannot see a scope granted, revoked or added since — and it is already
   * provably stale in production: on 2026-08-27 both live Shopify connections
   * stored `read_customers,read_orders,write_products` while `shopify.app.toml`
   * declares five required scopes. `currentAppInstallation.accessScopes` is the
   * only authority.
   *
   * The query was validated against the 2026-04 Admin schema by Shopify's own
   * validator (`@shopify/dev-mcp` `validate_graphql_codeblocks`, artifact
   * artifact-13166159-d290-4f43-9a02-3c643a541a12) on 2026-08-27 — not retyped
   * from memory. `AppInstallation.accessScopes` returns granted scopes only, with
   * no flag separating required from optional, which is why the declared sets are
   * read off `App` in the same round trip.
   * https://shopify.dev/docs/apps/build/authentication-authorization/manage-access-scopes
   */
  async getShopifyAccessScopes(
    target: Required<Pick<ShopifyConnectionTarget, 'connectionId'>> &
      Pick<ShopifyConnectionTarget, 'workspaceId'>,
  ): Promise<ServiceResponse<ShopifyScopeSnapshot>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke('shopify-admin-proxy', {
        body: {
          shopifyApiUrlPath: '/admin/api/2026-04/graphql.json',
          method: 'POST',
          connection_id: target.connectionId,
          brand_kit_id: target.workspaceId ?? undefined,
          body: { query: SHOPIFY_ACCESS_SCOPES_QUERY },
        },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Failed to read Shopify permissions');
      }

      const payload = data.data as
        | {
            data?: {
              currentAppInstallation?: {
                accessScopes?: Array<{ handle?: string }> | null;
                app?: {
                  requestedAccessScopes?: Array<{ handle?: string }> | null;
                  optionalAccessScopes?: Array<{ handle?: string }> | null;
                } | null;
              } | null;
            };
            errors?: Array<{ message?: string }>;
          }
        | undefined;

      // A 200 carrying a GraphQL `errors` entry is Shopify's shape for a partial
      // or refused read. Treating it as an empty scope list would render every
      // card as "not granted" on what is really an unanswered question.
      const firstError = payload?.errors?.[0]?.message;
      if (firstError) throw new Error(firstError);

      const installation = payload?.data?.currentAppInstallation;
      if (!installation) {
        throw new Error('Shopify did not return an app installation for this store');
      }

      const handles = (list: Array<{ handle?: string }> | null | undefined): string[] =>
        (list ?? [])
          .map((entry) => entry?.handle)
          .filter((handle): handle is string => typeof handle === 'string');

      return {
        granted: handles(installation.accessScopes),
        declaredRequired: handles(installation.app?.requestedAccessScopes),
        declaredOptional: handles(installation.app?.optionalAccessScopes),
      };
    }, 'Failed to read Shopify permissions');
  }

  /**
   * Live-checks a Meta connection's ACTUAL granted permissions
   * (`check-connection-scopes` edge function → Graph `/me/permissions`) and
   * persists the result to `user_connections.granted_scopes`/
   * `declined_scopes`/`scopes_checked_at`. This is the manual "check" action —
   * it brings an existing connection up to date without a reconnect. Only
   * meta-ads/facebook/instagram have a live recheck source; the edge function
   * refuses honestly for every other platform rather than guessing.
   */
  async checkConnectionScopes(
    connectionId: string,
  ): Promise<ServiceResponse<ConnectionScopeCheckResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke('check-connection-scopes', {
        body: { connection_id: connectionId },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Failed to check permissions');
      }

      return {
        grantedScopes: Array.isArray(data.granted_scopes) ? data.granted_scopes : [],
        declinedScopes: Array.isArray(data.declined_scopes) ? data.declined_scopes : [],
        checkedAt: typeof data.scopes_checked_at === 'string' ? data.scopes_checked_at : null,
      };
    }, 'Failed to check permissions');
  }

  /**
   * W3's move flow (docs/superpowers/specs/
   * 2026-08-23-workspace-tenancy-and-membership-design.md §3): moves ONE
   * connection — and, via `connection_id` FK, everything 40 tables carry
   * against it — into a different workspace on the same account. Preserves
   * the connection id and never changes `is_active`. If the connection is
   * active, it displaces the target workspace's existing active connection
   * for that platform (W2: one active connection per workspace+platform).
   *
   * Thin wrapper over `transfer-connection-to-workspace`
   * (service_role-only RPC caller — see that function's header). The user id
   * is derived server-side from the caller's verified JWT, never sent here.
   */
  async transferConnectionToWorkspace(
    connectionId: string,
    targetBrandKitId: string,
  ): Promise<ServiceResponse<TransferConnectionResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke(
        'transfer-connection-to-workspace',
        { body: { connectionId, targetBrandKitId } },
      );

      if (error || !data?.success) {
        // Same swallowed-body failure mode as check-connection-scopes above,
        // except the honest message here (produced by core.ts's
        // mapTransferRpcError) is the whole point of calling this at all —
        // `error?.message` alone is the generic FunctionsHttpError constant
        // on any non-2xx response, not the edge function's mapped reason.
        const message = await ServiceError.extractEdgeFunctionMessage(
          error,
          data?.error ?? 'Could not move this connection',
        );
        throw new Error(message);
      }

      return {
        connectionId: data.connectionId,
        fromBrandKitId: data.fromBrandKitId,
        toBrandKitId: data.toBrandKitId,
      };
    }, 'Could not move this connection');
  }

  /**
   * Sets whether ONE connection's revenue counts toward a future blended
   * revenue/ROAS/MER figure. See `src/lib/platforms/revenue-rollup.ts` for why
   * this lives in `metadata` rather than a new column, and for the standing
   * caveat that nothing reads this flag yet.
   *
   * Read-then-write rather than a partial JSON patch: Supabase's REST update
   * replaces the whole `metadata` column, so the current value is fetched
   * first and merged, never blindly overwritten. `user_connections` UPDATE is
   * owner-only by RLS (`user_connections_owner_update`, verified against
   * `pg_policies` on 2026-08-27), so a workspace MEMBER's attempt fails here
   * with a normal `ServiceResponse` error rather than a silent no-op.
   */
  async setConnectionRevenueRollupInclusion(
    connectionId: string,
    include: boolean,
  ): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      const { data: existing, error: fetchError } = await this.client
        .from('user_connections')
        .select('metadata')
        .eq('id', connectionId)
        .eq('user_id', ownerId)
        .maybeSingle();

      if (fetchError) {
        throw new Error(fetchError.message || 'Failed to read the current connection');
      }
      if (!existing) {
        throw new Error('This connection could not be found for your account');
      }

      const { error: updateError } = await this.client
        .from('user_connections')
        .update({
          metadata: withRevenueRollupInclusion(existing.metadata, include) as unknown as Json,
        })
        .eq('id', connectionId)
        .eq('user_id', ownerId);

      if (updateError) {
        throw new Error(updateError.message || 'Failed to update the revenue rollup setting');
      }
    }, 'Failed to update the revenue rollup setting');
  }

  async refreshShopifyConnection(): Promise<ServiceResponse<RefreshResult>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke('refresh-shopify-connection', {
        body: { userId },
      });

      if (error) {
        throw new Error(error.message || 'Failed to refresh Shopify connection');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to refresh Shopify connection');
      }

      return {
        success: true,
        message: 'Successfully refreshed your Shopify store connection.',
      };
    }, 'Failed to refresh Shopify connection');
  }

  /**
   * Refresh a Google Analytics connection
   */
  async refreshGoogleAnalyticsConnection(): Promise<ServiceResponse<RefreshResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke('refresh-ga4-connection', {
        // The Edge Function derives both actor and account owner from the
        // verified JWT. Never let a caller nominate another account id.
        body: {},
      });

      if (error) {
        throw new Error(error.message || 'Failed to refresh Google Analytics connection');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to refresh Google Analytics connection');
      }

      return {
        success: true,
        message: 'Successfully refreshed your Google Analytics connection.',
      };
    }, 'Failed to refresh Google Analytics connection');
  }

  /**
   * Sync social analytics for specific platforms
   */
  async syncSocialAnalytics(
    platforms: SocialAnalyticsSyncPlatform[],
    force = true,
  ): Promise<ServiceResponse<RefreshResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke('sync-social-analytics', {
        body: {
          force, // Force refresh even if recently synced
          platforms, // Only sync the specific platforms
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to sync social analytics');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to sync social analytics');
      }

      return {
        success: true,
        message: `Successfully refreshed account data from ${platforms.join(', ')}.`,
      };
    }, 'Failed to sync social analytics');
  }

  /**
   * Refresh a specific platform connection
   */
  async refreshPlatformConnection(
    platform: PersistedConnectionPlatform,
  ): Promise<ServiceResponse<RefreshResult>> {
    if (platform === 'shopify') {
      return this.refreshShopifyConnection();
    } else if (platform === 'google-analytics') {
      return this.refreshGoogleAnalyticsConnection();
    } else if (isSocialAnalyticsSyncPlatform(platform)) {
      return this.syncSocialAnalytics([platform]);
    }

    return this.execute(async () => {
      throw new Error(`No manual analytics sync is available for ${platform}.`);
    }, 'Manual sync is unavailable for this integration');
  }

  /**
   * Get platform configuration
   */
  getPlatformConfig(): Record<
    string,
    {
      label: string;
      description: string;
      badge?: string;
      color: string;
      singleConnectionNotice?: string;
      gradientClass: string;
      cardClass: string;
      iconBgClass: string;
      order: number;
      category: string;
    }
  > {
    return {
      facebook: {
        label: getPlatformLabel('facebook'),
        description: 'Publish and manage Facebook Pages',
        badge: 'Recommended',
        color: 'facebook',
        gradientClass: 'from-facebook to-facebook/80',
        cardClass: 'border-facebook/30 hover:border-facebook/50',
        iconBgClass: 'bg-facebook/10',
        order: 1,
        category: 'Organic social',
      },
      'meta-ads': {
        label: getPlatformLabel('meta-ads'),
        description: 'Measure paid campaign performance',
        badge: 'Ads',
        color: 'primary',
        gradientClass: 'from-primary/10 to-primary/5',
        cardClass: 'border-border/50 hover:border-border/80',
        iconBgClass: 'bg-primary/10',
        order: 1,
        category: 'Ad platforms',
      },
      'google-ads': {
        label: getPlatformLabel('google-ads'),
        description: 'Track spend and campaign performance',
        badge: 'Ads',
        color: 'primary',
        gradientClass: 'from-primary/10 to-primary/5',
        cardClass: 'border-border/50 hover:border-border/80',
        iconBgClass: 'bg-muted',
        order: 2,
        category: 'Ad platforms',
      },
      'linkedin-ads': {
        label: getPlatformLabel('linkedin-ads'),
        // Distinct from 'linkedin' below on purpose (2026-09-10): a real user
        // connected without being asked "ad account or personal?" because the
        // two tiles read identically. They are genuinely separate
        // connections — separate tokens, separate consent — and LinkedIn
        // fails its ENTIRE authorize request if any one requested scope is
        // unapproved, so they cannot be merged into one picker (see
        // docs/integrations/OAUTH-SCOPES-LEDGER.md §4). This line is the
        // honest way to tell them apart before clicking, not a combined flow.
        description: 'Connects your ad account',
        badge: 'Ads',
        color: 'linkedin',
        gradientClass: 'from-linkedin/10 to-linkedin/5',
        cardClass: 'border-linkedin/30 hover:border-linkedin/50',
        iconBgClass: 'bg-linkedin/10',
        order: 3,
        category: 'Ad platforms',
      },
      instagram: {
        label: getPlatformLabel('instagram'),
        description: 'Publish and measure Instagram content',
        badge: 'Popular',
        color: 'instagram',
        gradientClass: 'from-instagram to-instagram/80',
        cardClass: 'border-instagram/30 hover:border-instagram/50',
        iconBgClass: 'bg-instagram/10',
        order: 2,
        category: 'Organic social',
      },
      linkedin: {
        label: getPlatformLabel('linkedin'),
        // See the comment on 'linkedin-ads' above — same distinction, told
        // from this side.
        description: 'Publishes to your profile and Pages',
        badge: 'Business',
        color: 'linkedin',
        gradientClass: 'from-linkedin to-linkedin/80',
        cardClass: 'border-linkedin/30 hover:border-linkedin/50',
        iconBgClass: 'bg-linkedin/10',
        order: 3,
        category: 'Organic social',
      },
      tiktok: {
        label: getPlatformLabel('tiktok'),
        description: 'Publish short-form video content',
        badge: 'Trending',
        color: 'tiktok',
        gradientClass: 'from-tiktok to-tiktok/80',
        cardClass: 'border-tiktok/30 hover:border-tiktok/50',
        iconBgClass: 'bg-tiktok/10',
        order: 4,
        category: 'Organic social',
      },
      youtube: {
        label: getPlatformLabel('youtube'),
        description: 'Track channel and audience growth',
        badge: 'Analytics',
        color: 'primary',
        singleConnectionNotice: 'Only one YouTube channel can be connected per account.',
        gradientClass: 'from-primary/10 to-primary/5',
        cardClass: 'border-border/50 hover:border-border/80',
        iconBgClass: 'bg-muted',
        order: 5,
        category: 'Organic social',
      },
      pinterest: {
        label: getPlatformLabel('pinterest'),
        description: 'Track boards, pins, and performance',
        badge: 'Analytics',
        color: 'primary',
        gradientClass: 'from-primary/10 to-primary/5',
        cardClass: 'border-border/50 hover:border-border/80',
        iconBgClass: 'bg-primary/10',
        order: 6,
        category: 'Organic social',
      },
      shopify: {
        label: getPlatformLabel('shopify'),
        description: 'Sync products, orders, and revenue',
        badge: 'E-commerce',
        color: 'shopify',
        singleConnectionNotice: 'Only one Shopify store can be connected per workspace.',
        gradientClass: 'from-shopify to-shopify/80',
        cardClass: 'border-shopify/30 hover:border-shopify/50',
        iconBgClass: 'bg-shopify/10',
        order: 7,
        category: 'Store and tools',
      },
      'google-analytics': {
        label: getPlatformLabel('google-analytics'),
        description: 'Understand website traffic and audiences',
        badge: 'Analytics',
        color: 'primary',
        gradientClass: 'from-primary/10 to-primary/5',
        cardClass: 'border-border/50 hover:border-border/80',
        iconBgClass: 'bg-primary/10',
        order: 8,
        category: 'Store and tools',
      },
      'google-drive': {
        label: getPlatformLabel('google-drive'),
        description: 'Import media from Google Drive',
        badge: 'Storage',
        color: 'primary',
        gradientClass: 'from-primary/10 to-primary/5',
        cardClass: 'border-border/50 hover:border-border/80',
        iconBgClass: 'bg-primary/10',
        order: 9,
        category: 'Store and tools',
      },
      slack: {
        label: getPlatformLabel('slack'),
        description: 'Send links and files to Sources',
        badge: 'Team',
        color: 'primary',
        gradientClass: 'from-primary/10 to-primary/5',
        cardClass: 'border-border/50 hover:border-border/80',
        iconBgClass: 'bg-primary/10',
        order: 11,
        category: 'Store and tools',
      },
      // Gated behind VITE_ENABLE_GOOGLE_BUSINESS until the business.manage scope
      // is live on the OAuth consent screen AND the Business Profile (v4 reviews)
      // API access request is approved — otherwise the connect flow dead-ends at
      // Google's consent screen. Flip the flag on once both are in place.
      ...(import.meta.env.VITE_ENABLE_GOOGLE_BUSINESS === 'true' &&
      isPersistedConnectionPlatform('google-business')
        ? {
            'google-business': {
              label: getPlatformLabel('google-business'),
              description: 'Manage Google reviews and replies',
              badge: 'Reviews',
              color: 'primary',
              gradientClass: 'from-primary/10 to-primary/5',
              cardClass: 'border-border/50 hover:border-border/80',
              iconBgClass: 'bg-primary/10',
              order: 10,
              category: 'Store and tools',
            },
          }
        : {}),
    };
  }
}

// Export singleton instance
export const integrationService = IntegrationService.getInstance();
